import {
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import ParticipantsSideBar from "./views/participantsSideBar";
import Tables from "./views/tables";
import {
    NCoreUIKitLocalize,
    NCoreUIKitToast,
    NCoreUIKitTheme,
    PageContainer,
    StateCard,
    TextInput,
    Dialog,
    Button,
    Text
} from "ncore-ui-kit";
import type {
    SubEventSummaryType
} from "../../services/restAPI/actions/getAllSubEventsForAdmin/type";
import type {
    ThreeTParticipantDTO
} from "../../services/restAPI/actions/threeTGetParticipantsForAdmin/type";
import {
    ChevronRightIcon,
    AlertCircleIcon,
    ChevronLeftIcon,
    ArrowRightIcon,
    RefreshCwIcon,
    ArrowLeftIcon,
    CalendarIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import RESTServiceContext from "../../services/restAPI/context";

const ThreeT = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const [
        isFinishEventDialogVisible,
        setIsFinishEventDialogVisible
    ] = useState(false);

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const [
        pastSubEvents,
        setPastSubEvents
    ] = useState<SubEventSummaryType[]>([]);

    const [
        isHistoryMode,
        setIsHistoryMode
    ] = useState(false);

    const [
        isLoading,
        setIsLoading
    ] = useState(true);

    const [
        participantsSideBarVisible,
        setParticipantsSideBarVisible
    ] = useState(false);

    const [
        roundEndTime,
        setRoundEndTime
    ] = useState<number | null>(null);

    const [
        eventSubID,
        setEventSubID
    ] = useState<string | null>(null);

    const [
        eventID,
        setEventID
    ] = useState<string | null>(null);

    const [
        participants,
        setParticipants
    ] = useState<ThreeTParticipantDTO[]>([]);

    const [
        selectedParticipants,
        setSelectedParticipants
    ] = useState<ThreeTParticipantDTO[]>([]);

    const [
        tableCapacity,
        setTableCapacity
    ] = useState("");

    const [
        tableCount,
        setTableCount
    ] = useState("");

    const [
        roundDuration,
        setRoundDuration
    ] = useState("");

    const [
        tables,
        setTables
    ] = useState<string[][]>([]);

    const [
        totalRoundCount,
        setTotalRoundCount
    ] = useState(0);

    const [
        currentRound,
        setCurrentRound
    ] = useState(0);

    const [
        listedRound,
        setListedRound
    ] = useState(0);

    useEffect(() => {
        if (eventSubID) {
            AsyncStorage.getItem(`@ThreeT_TableConfig_${eventSubID}`).then(data => {
                if (data) {
                    try {
                        const parsed = JSON.parse(data);
                        if (parsed.tableCount) setTableCount(parsed.tableCount);
                        if (parsed.tableCapacity) setTableCapacity(parsed.tableCapacity);
                    } catch {
                        // ignore
                    }
                }
            });
        }
    }, [
        eventSubID
    ]);

    useEffect(() => {
        if (eventSubID && (tableCount || tableCapacity)) {
            AsyncStorage.setItem(`@ThreeT_TableConfig_${eventSubID}`, JSON.stringify({
                tableCapacity,
                tableCount
            })).catch(() => {});
        }
    }, [
        tableCapacity,
        eventSubID,
        tableCount
    ]);

    const [
        timeRemaining,
        setTimeRemaining
    ] = useState<number | null>(null);

    const [
        fetchState,
        setFetchState
    ] = useState<"LOADING" | "NO_SERVICE" | "NO_ACTIVE_EVENT" | "SUCCESS" | "ERROR">("LOADING");

    const [
        isPreviewMode,
        setIsPreviewMode
    ] = useState(false);

    const [
        hasFinished,
        setHasFinished
    ] = useState(false);

    const [
        activeSubEventStatus,
        setActiveSubEventStatus
    ] = useState<SubEventSummaryType["status"] | "">("");

    const initData = async () => {
        setIsLoading(true);
        setIsHistoryMode(false);
        try {
            const eventsResponse = await RESTServiceContext.action("getAllEventsForAdmin", null);
            if (eventsResponse.code === 200 && eventsResponse.payload) {
                const threeTEvent = eventsResponse.payload.find(e => e.moduleName === "threeT" || e.moduleName === "threet.service");

                if (threeTEvent) {
                    setEventID(threeTEvent.eventID);
                    // For sub events, we might need to get sub events to find the exact ID.
                    const subEventsResponse = await RESTServiceContext.action("getAllSubEventsForAdmin", {
                        eventID: threeTEvent.eventID
                    });

                    if (subEventsResponse.code === 200 && subEventsResponse.payload && subEventsResponse.payload.length > 0) {
                        const finishedEvents = subEventsResponse.payload.filter(se => se.status === "finished");
                        setPastSubEvents(finishedEvents);

                        const targetSubEvent = subEventsResponse.payload.find(se => se.status !== "finished");

                        if (targetSubEvent) {
                            setEventSubID(targetSubEvent.eventSubID);
                            setActiveSubEventStatus(targetSubEvent.status);

                            const allParticipants = await fetchParticipants(threeTEvent.eventID, targetSubEvent.eventSubID);
                            await fetchCurrentRound(threeTEvent.eventID, targetSubEvent.eventSubID, 0, false, allParticipants);
                            setFetchState("SUCCESS");
                        } else {
                            setFetchState("NO_ACTIVE_EVENT");
                        }
                    } else {
                        setFetchState("NO_ACTIVE_EVENT");
                    }
                } else {
                    setFetchState("NO_SERVICE");
                }
            } else {
                setFetchState("ERROR");
            }
        } catch {
            setFetchState("ERROR");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectPastEvent = async (pastEvent: SubEventSummaryType) => {
        if (!eventID) return;
        setIsLoading(true);
        setEventSubID(pastEvent.eventSubID);
        setIsHistoryMode(true);
        setFetchState("SUCCESS");
        const allParticipants = await fetchParticipants(eventID, pastEvent.eventSubID);
        await fetchCurrentRound(eventID, pastEvent.eventSubID, 1, true, allParticipants);
        setIsLoading(false);
    };

    const fetchParticipants = async (eID: string, esID: string) => {
        try {
            const response = await RESTServiceContext.action("threeTGetParticipantsForAdmin", {
                eventSubID: esID,
                eventID: eID
            });
            if (response.code === 200 && response.payload) {
                setParticipants(response.payload);

                try {
                    const storedSelections = await AsyncStorage.getItem(`@ThreeT_SelectedParticipants_${esID}`);
                    if (storedSelections) {
                        const parsedIds = JSON.parse(storedSelections) as string[];
                        const previouslySelected = response.payload.filter(p => parsedIds.includes(p.id));
                        setSelectedParticipants(previouslySelected);
                    }
                } catch {
                    // Ignore storage errors
                }
                return response.payload;
            }
            return [];
        } catch {
            console.error("ThreeT API error");
            return [];
        }
    };

    const fetchCurrentRound = async (eID: string, esID: string, targetRound = 0, isBrowsingHistory = false, allParticipants?: ThreeTParticipantDTO[]) => {
        try {
            const response = await RESTServiceContext.action("threeTGetEventRound", {
                roundNo: targetRound,
                eventSubID: esID,
                eventID: eID
            });
            if (response.code === 200 && response.payload) {
                setTotalRoundCount(response.payload.totalRoundCount || 0);

                if (!isBrowsingHistory) {
                    setCurrentRound(response.payload.roundNumber || 0);
                }

                setListedRound(response.payload.roundNumber);
                setTables(response.payload.tables);
                if (response.payload.endTime) {
                    setRoundEndTime(response.payload.endTime);
                } else {
                    setRoundEndTime(null);
                }
                if (response.payload.baseRoundDuration !== undefined) {
                    setRoundDuration(response.payload.baseRoundDuration.toString());
                }

                if (response.payload.participantIDs && allParticipants && response.payload.participantIDs.length > 0) {
                    const participantIDs = response.payload.participantIDs;
                    const selected = allParticipants.filter(p => participantIDs.includes(p.id));
                    setSelectedParticipants(selected);
                }

                if (response.payload.tableCount !== undefined) {
                    setTableCount(response.payload.tableCount.toString());
                }

                if (response.payload.tableCapacity !== undefined) {
                    setTableCapacity(response.payload.tableCapacity.toString());
                }
                if (response.payload.isFinished) {
                    setHasFinished(true);
                } else {
                    setHasFinished(false);
                }

                setIsPreviewMode(false);
            }
        } catch {
            console.error("ThreeT API error");
        }
    };

    useEffect(() => {
        initData();
    }, []);

    useEffect(() => {
        const updateTimer = () => {
            if (!eventSubID) return;

            if (roundEndTime) {
                const now = Date.now();
                const diff = roundEndTime - now;

                if (diff <= 0) {
                    setTimeRemaining(0);
                } else {
                    setTimeRemaining(Math.floor(diff / 1000));
                }
            } else {
                setTimeRemaining(null);
            }
        };

        updateTimer();
        const interval = setInterval(updateTimer, 1000);

        return () => clearInterval(interval);
    }, [
        eventSubID,
        currentRound,
        roundEndTime
    ]);

    const handleGeneratePlan = async () => {
        if (!eventID || !eventSubID) return;
        setIsSubmitting(true);
        try {
            const response = await RESTServiceContext.action("threeTGenerateSeatingPlan", {
                participantIDs: selectedParticipants.map(p => p.id),
                roundDuration: parseInt(roundDuration) || 0,
                tableCapacity: parseInt(tableCapacity) || 4,
                tableCount: parseInt(tableCount) || 10,
                eventSubID,
                eventID
            });

            if (response.code === 200 || response.code === 201) {
                if (response.payload) {
                    setTotalRoundCount(response.payload.totalRoundCount);
                    setListedRound(1);
                    setCurrentRound(1);
                    setTables(response.payload.tables);

                    const payloadObj = response.payload as Record<string, unknown>;
                    if (payloadObj.endTime || payloadObj.previewEndTime) {
                        setRoundEndTime((payloadObj.endTime as number) || (payloadObj.previewEndTime as number));
                    } else {
                        setRoundEndTime(null);
                    }
                    setIsPreviewMode(true);
                }
            } else {
                NCoreUIKitToast.open({
                    title: localize("threet-generate-plan-error"),
                    type: "danger"
                });
            }
        } catch {
            NCoreUIKitToast.open({
                title: localize("threet-generate-plan-error"),
                type: "danger"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleStartRound = async () => {
        if (!eventID || !eventSubID) return;
        setIsSubmitting(true);
        try {
            if (!isPreviewMode) {
                const generateResponse = await RESTServiceContext.action("threeTGenerateSeatingPlan", {
                    participantIDs: selectedParticipants.map(p => p.id),
                    roundDuration: parseInt(roundDuration) || 0,
                    tableCapacity: parseInt(tableCapacity) || 4,
                    tableCount: parseInt(tableCount) || 10,
                    eventSubID,
                    eventID
                });

                if (generateResponse.code !== 200 && generateResponse.code !== 201) {
                    NCoreUIKitToast.open({
                        title: localize("global-error"),
                        type: "danger"
                    });
                    setIsSubmitting(false);
                    return;
                }
            }

            if (activeSubEventStatus !== "continue") {
                const startEventResponse = await RESTServiceContext.action("startEvent", {
                    targetDate: new Date().toISOString(),
                    subEventID: eventSubID,
                    eventID
                });

                if (startEventResponse.code !== 200 && startEventResponse.code !== 201) {
                    NCoreUIKitToast.open({
                        title: localize("global-error") || "Etkinlik başlatılamadı",
                        type: "danger"
                    });
                    setIsSubmitting(false);
                    return;
                }

                setActiveSubEventStatus("continue");
            }

            const response = await RESTServiceContext.action("threeTStartRound", {
                eventSubID,
                eventID
            });

            if (response.code === 200 || response.code === 201) {
                if (response.payload) {
                    setIsPreviewMode(false);
                    fetchCurrentRound(eventID, eventSubID, response.payload.currentRound);
                }
            } else {
                NCoreUIKitToast.open({
                    title: localize("global-error"),
                    type: "danger"
                });
            }
        } catch {
            NCoreUIKitToast.open({
                title: localize("global-error"),
                type: "danger"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFinish = async () => {
        if (!eventID || !eventSubID) return;
        setIsSubmitting(true);
        try {
            const response = await RESTServiceContext.action("threeTFinish", {
                eventSubID,
                eventID
            });

            if (response.code === 200 || response.code === 201) {
                setRoundEndTime(null);
                setTimeRemaining(null);
                setHasFinished(true);

                NCoreUIKitToast.open({
                    title: localize("threet-finished"),
                    type: "success"
                });
            }
        } catch {
            NCoreUIKitToast.open({
                title: localize("global-error"),
                type: "danger"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFinishEvent = async () => {
        if (!eventID || !eventSubID) return;
        setIsSubmitting(true);
        try {
            await RESTServiceContext.action("finishEvent", {
                targetDate: new Date().toISOString(),
                subEventID: eventSubID,
                eventID
            });
            NCoreUIKitToast.open({
                title: localize("global-success"),
                type: "success"
            });
            setIsFinishEventDialogVisible(false);
            await initData();
        } catch {
            NCoreUIKitToast.open({
                title: localize("global-error"),
                type: "danger"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return <PageContainer
            isScrollable={true}
        >
            <View
                style={stylesheet.loadingContainer}
            >
                <ActivityIndicator
                    size="large"
                />
            </View>
        </PageContainer>;
    }

    if (fetchState === "NO_ACTIVE_EVENT" || fetchState === "NO_SERVICE") {
        return <PageContainer
            isScrollable={true}
        >
            <View
                style={[
                    stylesheet.stateCardContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <StateCard
                    title={localize(fetchState === "NO_ACTIVE_EVENT" ? "threet-active-event-not-found" : "threet-service-not-found")}
                    icon={AlertCircleIcon}
                    type="neutral"
                />
            </View>
            {
                pastSubEvents.length > 0 && (
                    <View
                        style={{
                            paddingHorizontal: spaces.spacingXl,
                            paddingBottom: spaces.spacingXl,
                            gap: spaces.spacingMd
                        }}
                    >
                        <Text
                            variant="titleMediumSize"
                        >
                            Geçmiş Etkinlikler
                        </Text>
                        <View
                            style={[
                                stylesheet.pastEventsContainer,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            {
                                pastSubEvents.map((pastEvent, index) => {
                                    const dateObj = new Date(pastEvent.startDate);
                                    let formattedDate = pastEvent.startDate || "Tarih Belirtilmemiş";

                                    if (!isNaN(dateObj.getTime())) {
                                        formattedDate = dateObj.toLocaleString("tr-TR", {
                                            timeStyle: "short",
                                            dateStyle: "short"
                                        });
                                    }

                                    return <TouchableOpacity
                                        onPress={() => handleSelectPastEvent(pastEvent)}
                                        style={{
                                            backgroundColor: colors.project.brandWhite,
                                            paddingHorizontal: spaces.spacingLg,
                                            paddingVertical: spaces.spacingMd,
                                            justifyContent: "space-between",
                                            borderRadius: radiuses.md,
                                            flexDirection: "row",
                                            alignItems: "center"
                                        }}
                                        key={`past-event-${index}`}
                                    >
                                        <View
                                            style={{
                                                flexDirection: "row",
                                                alignItems: "center",
                                                gap: spaces.spacingMd
                                            }}
                                        >
                                            <CalendarIcon
                                                color={colors.content.icon.high}
                                                size={20}
                                            />
                                            <Text
                                                customColor={colors.content.text.high}
                                                variant="bodyMediumSize"
                                            >
                                                {formattedDate} - {pastEvent.eventLocation || "Lokasyon Bilinmiyor"}
                                            </Text>
                                        </View>
                                        <ArrowRightIcon
                                            color={colors.content.icon.high}
                                            size={20}
                                        />
                                    </TouchableOpacity>;
                                })
                            }
                        </View>
                    </View>)
            }
        </PageContainer>;
    }

    if (fetchState === "ERROR") {
        return <PageContainer
            isScrollable={true}
        >
            <View
                style={[
                    stylesheet.errorContainer,
                    {
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <StateCard
                    title={localize("global-error")}
                    icon={AlertCircleIcon}
                    type="danger"
                />
            </View>
        </PageContainer>;
    }

    const isRoundStarted = roundEndTime !== null && !isPreviewMode;
    const nextRoundToStart = isRoundStarted ? currentRound + 1 : (currentRound === 0 ? 1 : currentRound);

    const isStartRoundDisabled = totalRoundCount === 0 || tables.length === 0;
    const isPrevDisabled = listedRound <= 1;
    const isNextDisabled = isHistoryMode ? listedRound >= totalRoundCount : listedRound >= currentRound;
    const isFinishedMode = hasFinished || (currentRound >= totalRoundCount && isRoundStarted && totalRoundCount !== 0);
    const isEventActive = currentRound > 0 && !hasFinished;

    const renderBottomActions = () => <View
        style={[
            stylesheet.bottomActionContainer,
            {
                bottom: !isMobile ? spaces.spacingXl : undefined,
                paddingBottom: !isMobile ? spaces.spacingXl : 0,
                marginTop: isMobile ? spaces.spacingXl * 2 : 0,
                position: isMobile ? "relative" : "absolute",
                right: !isMobile ? 0 : undefined,
                left: !isMobile ? 0 : undefined,
                gap: spaces.spacingMd
            }
        ]}
        pointerEvents="box-none"
    >
        <Button
            onPress={() => {
                if (eventID && eventSubID) {
                    fetchCurrentRound(eventID, eventSubID, listedRound - 1, true);
                }
            }}
            icon={({
                color,
                size
            }) => <ChevronLeftIcon
                color={colors.content.icon[color]}
                size={size}
            />}
            customBorderColor="secondaryButton"
            style={stylesheet.navigationButton}
            title={localize("threet-prev")}
            customColor="secondaryButton"
            isDisabled={isPrevDisabled}
            spreadBehaviour="free"
            variant="filled"
        />
        {
            !isHistoryMode && (
                    hasFinished ?
                        <Button
                            title={localize("events-detail-finish-event") || "Etkinliği Bitir"}
                            onPress={() => setIsFinishEventDialogVisible(true)}
                            isLoading={isSubmitting}
                        />
                    :
                        !isFinishedMode ?
                            <Button
                                title={nextRoundToStart === 1 ? localize("threet-start-first-round") : `${localize("threet-start-round")}: ${nextRoundToStart}`}
                                isDisabled={isStartRoundDisabled}
                                onPress={handleStartRound}
                                isLoading={isSubmitting}
                            />
                    :
                            <Button
                                title={localize("threet-finish")}
                                isLoading={isSubmitting}
                                onPress={handleFinish}
                            />)
        }
        <Button
            onPress={() => {
                if (eventID && eventSubID) {
                    fetchCurrentRound(eventID, eventSubID, listedRound + 1, true);
                }
            }}
            icon={({
                color,
                size
            }) => <ChevronRightIcon
                color={colors.content.icon[color]}
                size={size}
            />}
            customBorderColor="secondaryButton"
            style={stylesheet.navigationButton}
            title={localize("threet-next")}
            customColor="secondaryButton"
            isDisabled={isNextDisabled}
            spreadBehaviour="free"
            iconDirection="right"
            variant="filled"
        />
    </View>;

    return <PageContainer
        isScrollable={false}
    >
        <ParticipantsSideBar
            onRefresh={() => fetchParticipants(eventID!, eventSubID!)}
            setSelectedParticipants={setSelectedParticipants}
            selectedParticipants={selectedParticipants}
            setVisible={setParticipantsSideBarVisible}
            visible={participantsSideBarVisible}
            isSelectionDisabled={isEventActive}
            participants={participants}
            eventSubID={eventSubID}
            eventID={eventID}
        />
        <ScrollView
            contentContainerStyle={{
                padding: spaces.spacingLg,
                paddingBottom: spaces.spacingXl * 5
            }}
            showsVerticalScrollIndicator={false}
            style={{
                flex: 1
            }}
        >
            <View
                style={{
                    justifyContent: "space-between",
                    flexDirection: "row",
                    alignItems: "center"
                }}
            >
                <View
                    style={{
                        alignItems: "center",
                        flexDirection: "row",
                        gap: spaces.spacingMd
                    }}
                >
                    {
                        isHistoryMode && (
                            <Button
                                icon={() => <ArrowLeftIcon
                                    color={colors.project.brandWhite}
                                    size={24}
                                />}
                                customBorderColor="secondaryButton"
                                customColor="secondaryButton"
                                onPress={initData}
                                variant="filled"
                            />)
                    }
                    <Text
                        variant="titleLargeSize"
                    >
                        {localize("threet-page-title")} {isHistoryMode ? "(Geçmiş)" : ""}
                    </Text>
                </View>
                {
                    !isHistoryMode && (
                        <Button
                            icon={() => <RefreshCwIcon
                                color={colors.project.brandWhite}
                                size={20}
                            />
                            }
                            title={localize("threet-refresh")}
                            onPress={initData}
                        />)
                }
            </View>
            <View
                style={[
                    stylesheet.formContainer,
                    {
                        alignItems: isMobile ? "stretch" : "flex-end",
                        flexDirection: isMobile ? "column" : "row",
                        marginTop: spaces.spacingXl,
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={{
                        alignItems: isMobile ? "stretch" : "flex-end",
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingMd,
                        flexWrap: "wrap"
                    }}
                >
                    <TextInput
                        style={isMobile ? {} : stylesheet.inputContainer}
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        isDisabled={isEventActive || isHistoryMode}
                        customColor={colors.project.brandWhite}
                        title={localize("threet-table-count")}
                        onChangeText={setTableCount}
                        keyboardType="number-pad"
                        value={tableCount}
                    />
                    <TextInput
                        style={isMobile ? {} : stylesheet.inputContainer}
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        isDisabled={isEventActive || isHistoryMode}
                        customColor={colors.project.brandWhite}
                        title={localize("threet-chair-count")}
                        onChangeText={setTableCapacity}
                        keyboardType="number-pad"
                        value={tableCapacity}
                    />
                    <TextInput
                        style={isMobile ? {} : stylesheet.inputContainer}
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        title={localize("threet-round-duration-label")}
                        isDisabled={isEventActive || isHistoryMode}
                        customColor={colors.project.brandWhite}
                        onChangeText={setRoundDuration}
                        keyboardType="number-pad"
                        value={roundDuration}
                    />
                    {
                        !isHistoryMode && (
                            <View
                                style={[
                                    stylesheet.buttonWrapper,
                                    isMobile && {
                                        width: "100%"
                                    }
                                ]}
                            >
                                <Button
                                    spreadBehaviour={isMobile ? "stretch" : "free"}
                                    title={localize("threet-generate-plan")}
                                    onPress={handleGeneratePlan}
                                    isDisabled={isEventActive}
                                    isLoading={isSubmitting}
                                />
                            </View>)
                    }
                </View>
                <View
                    style={[
                        stylesheet.buttonWrapper,
                        isMobile && {
                            width: "100%"
                        }
                    ]}
                >
                    <Button
                        title={`${localize("threet-participants-title")} (${selectedParticipants.length}/${participants.length})`}
                        onPress={() => setParticipantsSideBarVisible(true)}
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        customTextColor={colors.project.brandWhite}
                        customBorderColor="secondaryButton"
                        customColor="secondaryButton"
                        variant="filled"
                    />
                </View>
            </View>
            <View
                style={[
                    stylesheet.actionContainer,
                    {
                        alignItems: isMobile ? "flex-start" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        backgroundColor: "transparent",
                        marginTop: spaces.spacingLg,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingMd,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.roundInfoContainer,
                        {
                            alignItems: isMobile ? "flex-start" : "center",
                            flexDirection: isMobile ? "column" : "row",
                            gap: spaces.spacingLg
                        }
                    ]}
                >
                    <Text
                        variant="bodyMediumSize"
                    >
                        {localize("threet-active-round")}: {currentRound}
                    </Text>
                    {
                        timeRemaining !== null && listedRound === currentRound && !hasFinished && !isHistoryMode && (
                            <Text
                                customColor={timeRemaining === 0 ? colors.system.default.error : colors.project.gold}
                                variant="titleMediumSize"
                            >
                                {Math.floor(timeRemaining / 60).toString().padStart(2, "0")}:{(timeRemaining % 60).toString().padStart(2, "0")}
                            </Text>)
                    }
                    <Text
                        variant="bodyMediumSize"
                    >
                        {localize("threet-target-round")}: {totalRoundCount}
                    </Text>
                    <Text
                        variant="bodyMediumSize"
                    >
                        {localize("threet-listed-round")}: {listedRound}
                    </Text>
                </View>
            </View>
            <Tables
                participants={participants}
                tables={tables}
            />
            {isMobile && renderBottomActions()}
        </ScrollView>
        {!isMobile && renderBottomActions()}
        <Dialog
            title={localize("events-detail-finish-event") || "Etkinliği Bitir"}
            onOverlayPress={() => setIsFinishEventDialogVisible(false)}
            primaryButtonProps={{
                title: localize("members-form-dialog-yes") || "Evet",
                onPress: handleFinishEvent
            }}
            secondaryButtonProps={{
                title: localize("members-form-dialog-no") || "Hayır",
                onPress: () => setIsFinishEventDialogVisible(false),
                customBorderColor: "secondaryButton",
                customColor: "secondaryButton",
                variant: "filled"
            }}
            content={localize("threet-finish-event-confirm-title")}
            isVisible={isFinishEventDialogVisible}
            variant="yes-no"
        />
    </PageContainer>;
};

export default ThreeT;
